// src/components/LiveChat.tsx
import { motion, AnimatePresence } from "motion/react";
import { Avatar, Badge, Flex, Heading, IconButton, ScrollArea, Select, Text } from "@radix-ui/themes";
import { MdCancel, MdClose } from "react-icons/md";
import { ChatBubble, ReplyToBubble } from "./ChatBubble";
import { ChatInput } from "./ChatInput";
import { CreateConversationBox } from "./CreateConversationBox";
import TypingIndicator from "./TypingIndicator";
import { useLiveChat } from "./hook/useLiveChat";
import { useContext } from "react";
import {DashboardContext} from "@context/DashboardContext";
import { useUser } from "@/hooks/useUser";

function formatTimeAgo(value?: Date | string | null): string {
    if (!value) return "offline";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "offline";

    const diffMs = Date.now() - date.getTime();
    const diffSec = Math.max(0, Math.floor(diffMs / 1000));

    if (diffSec < 60) return "1m ago";
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}h ago`;
    const diffDay = Math.floor(diffHour / 24);
    return `${diffDay}d ago`;
}

export const LiveChat = () => {
    const { toggleLivechat, livechatOpen, livechatTargetUserId, setLivechatTargetUserId } = useContext(DashboardContext);
    const { user } = useUser();

    const {
        createConversation,
        recordTyping,
        sendMessage,
        deleteMessage,
        messages,
        adminUsers,
        adminThreads,
        isTyping,
        isAdmin,
        conversion,
        exist,
        register,
        handleSubmit,
        errors,
        isSubmitting,
        handleScroll,
        replyTo,
        setReplyTo,
    } = useLiveChat();

    const lastSeenDate = conversion?.user?.last_seen;
    const isOnline = !!lastSeenDate && Date.now() - new Date(lastSeenDate).getTime() <= 60 * 1000;
    const lastSeenText = isOnline
        ? `Online • ${formatTimeAgo(lastSeenDate)}`
        : `Last seen ${formatTimeAgo(lastSeenDate)}`;
    const customerName = conversion?.user?.name || "User";

    return (
        <>
            {livechatOpen && <motion.div
                initial={{ opacity: 0, x: 300 }}
                animate={{ opacity: 1, x: 0, }}
                exit={{ opacity: 0, x: 300 }}
                transition={{ duration: 0.3 }}
                className={`${livechatOpen ? '' : "hidden"} h-[100%] max-h-screen flex flex-col w-[400px] mobile:w-full fixed top-0 right-0 z-20 p-3 mobile:p-1`}>

                <form onSubmit={handleSubmit(createConversation)}
                    className="
                dark:bg-card-background-dark
                bg-card-background-light
                w-full h-full mobile:max-h-[100%] my-auto max-h-[95%] rounded-tl-xl 
                rounded-br-xl 
                backdrop-blur-sm
                flex flex-col
                dark:border-card-border-dark
                border-card-border-light
                border-2
                p-0
                transition-all
                ">
                    <div className="fixed top-0 flex-grow bg-[--gray-2] backdrop-blur-lg flex items-center justify-between pr-3 rounded-tl-xl h-[70px] px-3 z-10 w-full">
                        <Flex align={'center'} className="gap-3">
                            <Avatar fallback={"A"} radius="full" />
                            <div>
                                {isAdmin ? (
                                    <>
                                        <Select.Root
                                            value={livechatTargetUserId ? String(livechatTargetUserId) : "__none__"}
                                            onValueChange={(value) => {
                                                if (value === "__none__") {
                                                    setLivechatTargetUserId?.(null);
                                                    return;
                                                }
                                                setLivechatTargetUserId?.(Number(value));
                                            }}
                                        >
                                            <Select.Trigger className="min-w-[220px]" />
                                            <Select.Content className="max-h-[300px]">
                                                <Select.Item value="__none__">All conversations</Select.Item>
                                                {adminUsers.map((u) => {
                                                    const name = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || `User #${u.id}`;
                                                    return (
                                                        <Select.Item key={u.id} value={String(u.id)}>
                                                            {name}
                                                        </Select.Item>
                                                    );
                                                })}
                                            </Select.Content>
                                        </Select.Root>
                                        <Text color="gray" size={'1'}>
                                            {livechatTargetUserId
                                                ? lastSeenText
                                                : `${adminThreads.filter((t) => t.unreadCount > 0).length} unread chats`}
                                        </Text>
                                    </>
                                ) : (
                                    <>
                                        <Heading size={'3'}>Customer Support</Heading>
                                        <Text color="gray" size={'1'}>
                                            {isTyping ? "Typing" : "Typical reply: 1 min"}
                                        </Text>
                                    </>
                                )}
                            </div>
                        </Flex>
                        <IconButton type="button" onClick={() => {
                            if (isAdmin) {
                                setLivechatTargetUserId?.(null);
                            }
                            toggleLivechat!(false)
                        }} variant="soft" size={'3'} radius="full" color="gray">
                            <MdClose />
                        </IconButton>
                    </div>
                    <AnimatePresence initial={false}>
                        <ScrollArea id='chatlistContainer' onScroll={handleScroll} className="flex-grow pt-[60px]">
                            {/* <motion.div layout style={{ width: isOpen ? "80vw" : 0 }} /> */}
                            <motion.div layout style={{
                                justifyContent: exist ? 'flex-end' : 'center',
                                height: livechatOpen ? '100%' : '0'
                            }} className={`flex flex-col gap-3 pt-[10px] pb-[10px] px-[10px] h-full`}>
                                {isAdmin && !livechatTargetUserId && (
                                    <div className="grid gap-2">
                                        {adminThreads.length === 0 && (
                                            <Text color="gray" size="2">
                                                No conversations yet.
                                            </Text>
                                        )}
                                        {adminThreads.map((thread) => (
                                            <button
                                                key={thread.sessionId}
                                                type="button"
                                                onClick={() => setLivechatTargetUserId?.(thread.userId)}
                                                className="text-left border border-[--gray-a4] rounded-xl p-3 bg-[--gray-2] hover:bg-[--gray-3] transition-colors"
                                            >
                                                <Flex justify="between" align="center" className="mb-1">
                                                    <Text weight="bold" size="2">
                                                        {thread.name}
                                                    </Text>
                                                    <Flex align="center" gap="2">
                                                        {!!thread.unreadCount && (
                                                            <Badge color="red" size="1">
                                                                {thread.unreadCount} new
                                                            </Badge>
                                                        )}
                                                        <Text color="gray" size="1">
                                                            {thread.lastMessageAt ? formatTimeAgo(thread.lastMessageAt) : ""}
                                                        </Text>
                                                    </Flex>
                                                </Flex>
                                                <Text color="gray" size="1" className="line-clamp-1">
                                                    {thread.lastMessageFromAdmin ? "You: " : ""}
                                                    {thread.lastMessage || "No messages yet"}
                                                </Text>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {!isAdmin && (
                                    <CreateConversationBox
                                        errors={errors}
                                        isSubmitting={isSubmitting}
                                        register={register}
                                        conversationExist={exist}
                                        hideIdentityFields={!!user?.id}
                                    />
                                )}

                                {exist && (!isAdmin || !!livechatTargetUserId) &&
                                    messages.map((e, i) => {
                                        var replyToMsg = messages.find((r) => r.id  == e.reply_to);
                                        var replyToComponent = undefined;
                                        if (replyToMsg)
                                            replyToComponent = <ReplyToBubble
                                                viewerRole={isAdmin ? "admin" : "user"}
                                                counterpartName={customerName}
                                                replyTo={replyToMsg}
                                            />;
                                        
                                        return <motion.div
                                            key={e.id}>
                                            <ChatBubble
                                                viewerRole={isAdmin ? "admin" : "user"}
                                                counterpartName={customerName}
                                                replyToComponent={replyToComponent}
                                                onReply={(msg) => setReplyTo(msg)}
                                                onDelete={() => deleteMessage(e)}
                                                data={e}
                                            />
                                        </motion.div>;
                                    })
                                }
                                <TypingIndicator isTyping={isTyping} />
                            </motion.div>

                        </ScrollArea>
                    </AnimatePresence>

                    <div className="flex flex-col items-center bg-[--gray-1]">
                        {replyTo &&
                            <div className="px-2 w-[100%] flex items-start gap-4 pr-4">
                                <ReplyToBubble
                                    viewerRole={isAdmin ? "admin" : "user"}
                                    counterpartName={customerName}
                                    className={'mt-2'}
                                    replyTo={replyTo}
                                />
                                <IconButton autoFocus={false} type="button" onClick={() => setReplyTo(null)} variant="ghost" size={'3'} className="mt-2"><MdCancel /></IconButton>
                            </div>
                        }
                        {(!isAdmin || !!livechatTargetUserId) && (
                            <ChatInput onChanged={recordTyping} onSubmit={sendMessage} register={register} />
                        )}
                    </div>
                </form>
            </motion.div>}</>
    );
};
